// assets/hh-tillval.js
// Tillval på produktsidan, se docs/tillval.md, blocks/tillval.liquid och snippets/hh-tillval-grupp.liquid.
import { Component } from '@theme/component';

// Kontrolltecken och radbrytningar ska aldrig hamna i en gravyr eller ett önskemål.
const CONTROL_CHARACTERS = /[\u0000-\u001F\u007F]+/g;

/**
 * @typedef {object} CartAddItem
 * @property {number} id
 * @property {number} quantity
 * @property {number} [parent_id]
 * @property {number} [selling_plan]
 * @property {Record<string, string>} properties
 */

/**
 * @param {string} value
 * @returns {string}
 */
function cleanText(value) {
  return value.replace(CONTROL_CHARACTERS, ' ').replace(/\s+/g, ' ').trim();
}

/** @returns {string} */
function uniqueKey() {
  return Math.random().toString(36).slice(2, 10);
}

/**
 * En grupp i blocket = ett tillval (metaobjektet hh_tillval).
 * - data-kind="variant": val med pris, valet blir en underrad (kryssruta + knappar, eller rullgardin om obligatoriskt)
 * - data-kind="fritt-val": gratis val, sparas på produktraden
 * - data-kind="text": textrader, med data-avgift som underrad, annars på produktraden
 */
class TillvalGroup {
  /** @param {HTMLElement} element */
  constructor(element) {
    this.element = element;
    this.kind = element.dataset.kind ?? '';
    this.handle = element.dataset.tillval ?? '';
    this.rubrik = element.dataset.rubrik ?? '';
    this.avgift = Number(element.dataset.avgift) || 0;
    this.select = /** @type {HTMLSelectElement | null} */ (element.querySelector('[data-tillval-val]'));
    this.toggle = /** @type {HTMLInputElement | null} */ (element.querySelector('.checkbox__input'));
    this.rowsContainer = /** @type {HTMLElement | null} */ (element.querySelector('[data-tillval-rader]'));
    this.variantsContainer = /** @type {HTMLElement | null} */ (element.querySelector('[data-tillval-varianter]'));
    this.variants = /** @type {HTMLInputElement[]} */ ([...element.querySelectorAll('[data-tillval-variant]')]);
    this.rows = /** @type {HTMLInputElement[]} */ ([...element.querySelectorAll('[data-tillval-rad]')]);
  }

  /** Är tillvalet valt? Fritext utan reglage räknas som valt när något är ifyllt. */
  get isActive() {
    if (this.toggle) return this.toggle.checked;
    if (this.select) return this.select.value !== '';
    return true;
  }

  /** Underradens variant-id: vald knapp, rullgardinens val eller kryssrutans värde (en enda variant). */
  get variantId() {
    if (this.variants.length > 0) return Number(this.variants.find((input) => input.checked)?.value) || 0;
    return Number(this.select?.value ?? this.toggle?.value) || 0;
  }

  sync() {
    const active = this.isActive;
    if (this.variantsContainer) this.variantsContainer.hidden = !active;
    if (this.rowsContainer && (this.select || this.toggle)) this.rowsContainer.hidden = !active;

    for (const row of this.rows) {
      const rowsVisible = !this.rowsContainer?.hidden;
      row.disabled = !rowsVisible;
      row.required =
        rowsVisible && (row.dataset.kravs === 'alltid' || (row.dataset.kravs === 'aktiv' && active));
    }
  }

  /** @returns {Array<[string, string]>} */
  texts() {
    if (this.rowsContainer?.hidden) return [];
    return this.rows
      .map((row) => /** @type {[string, string]} */ ([row.dataset.label || row.id, cleanText(row.value)]))
      .filter(([, text]) => text !== '');
  }

  reset() {
    if (this.select) this.select.value = '';
    if (this.toggle) this.toggle.checked = false;
    for (const input of this.variants) input.checked = input.defaultChecked;
    for (const row of this.rows) row.value = '';
    this.sync();
  }
}

/**
 * Blocket Tillval. Produktformuläret frågar blocket efter en färdig begäran via buildPayload().
 */
class TillvalComponent extends Component {
  /** @type {TillvalGroup[]} */
  #groups = [];

  connectedCallback() {
    super.connectedCallback();
    this.#groups = [...this.querySelectorAll('[data-tillval-grupp]')].map(
      (element) => new TillvalGroup(/** @type {HTMLElement} */ (element))
    );
    // invalid bubblar inte, så den fångas i capture-fasen.
    this.addEventListener('invalid', this.#onInvalid, true);
    for (const group of this.#groups) group.sync();
    for (const row of this.#allRows) this.#updateCount(row);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('invalid', this.#onInvalid, true);
  }

  /** @param {Event} event */
  handleChange(event) {
    const group = this.#groupFor(/** @type {Element} */ (event.target));
    if (!group) return;
    group.sync();
    // Byte av knappval ska inte flytta fokus från knapparna
    if (event.target !== group.toggle && event.target !== group.select) return;
    if (group.isActive && group.rowsContainer && !group.rowsContainer.hidden) group.rows[0]?.focus();
  }

  /** @param {Event} event */
  handleInput(event) {
    const input = /** @type {HTMLInputElement} */ (event.target);
    input.setCustomValidity('');
    this.#updateCount(input);
  }

  /**
   * Bygger en begäran till /cart/add.js: produkten plus betalda tillval som underrader.
   * Returnerar null när inga tillval är valda, så att formuläret skickas som vanligt.
   *
   * @param {FormData} formData - Produktformulärets data.
   * @returns {{ items: CartAddItem[], sections: string } | null}
   */
  buildPayload(formData) {
    const id = Number(formData.get('id'));
    const quantity = Number(formData.get('quantity')) || 1;

    /** @type {Record<string, string>} */
    const parentProperties = {};
    for (const [key, value] of formData.entries()) {
      const match = /^properties\[(.+)\]$/.exec(key);
      if (match?.[1] && typeof value === 'string' && value.trim() !== '') parentProperties[match[1]] = value;
    }

    /** @type {CartAddItem[]} */
    const children = [];
    let hasTillval = false;

    for (const group of this.#groups) {
      if (!group.isActive) continue;
      const texts = group.texts();
      const tillvalProperties = { ...Object.fromEntries(texts), _tillval: group.handle };

      if (group.kind === 'variant') {
        if (!group.variantId) continue;
        children.push({ id: group.variantId, quantity, parent_id: id, properties: tillvalProperties });
      } else if (group.kind === 'text' && group.avgift) {
        if (texts.length === 0) continue;
        children.push({ id: group.avgift, quantity, parent_id: id, properties: tillvalProperties });
      } else {
        if (group.kind === 'fritt-val' && group.select) parentProperties[group.rubrik] = group.select.value;
        for (const [label, text] of texts) parentProperties[label] = text;
        if (group.kind === 'text' && texts.length === 0) continue;
      }
      hasTillval = true;
    }

    if (!hasTillval) return null;

    // Unikt dolt id: produkten med tillval ska aldrig slås ihop med en rad utan tillval.
    if (children.length > 0) parentProperties._tillval_id = uniqueKey();

    /** @type {CartAddItem} */
    const parent = { id, quantity, properties: parentProperties };
    const sellingPlan = Number(formData.get('selling_plan'));
    if (sellingPlan) parent.selling_plan = sellingPlan;

    return { items: [parent, ...children], sections: String(formData.get('sections') ?? '') };
  }

  /** Återställer blocket efter att produkten lagts i varukorgen. */
  reset() {
    for (const group of this.#groups) group.reset();
    for (const row of this.#allRows) this.#updateCount(row);
  }

  get #allRows() {
    return this.#groups.flatMap((group) => group.rows);
  }

  /** @param {Element} element */
  #groupFor(element) {
    const groupElement = element.closest('[data-tillval-grupp]');
    return this.#groups.find((group) => group.element === groupElement);
  }

  /** @param {HTMLInputElement} input */
  #updateCount(input) {
    const count = input.parentElement?.querySelector('[data-tillval-antal]');
    if (count) count.textContent = `${input.value.length}/${input.maxLength}`;
  }

  /** @param {Event} event */
  #onInvalid = (event) => {
    const input = /** @type {HTMLInputElement} */ (event.target);
    if (input.matches('[data-tillval-rad]') && input.validity.valueMissing && this.dataset.saknas) {
      input.setCustomValidity(this.dataset.saknas);
    }
  };
}

if (!customElements.get('hh-tillval-component')) {
  customElements.define('hh-tillval-component', TillvalComponent);
}
